import { isDebug, isMobileApp } from "@/constants";
import type { User } from "@dndbeyond/auth/types/user";
import { EventPipeline } from "@dndbeyond/event-pipeline-lib/client";
import {
  Platform,
  Region,
  Source,
  SubscriptionLevel,
  type GeoPlugin,
  type Plugin,
  type UserPlugin,
} from "@dndbeyond/event-pipeline-lib/shared";

const biVisitorIdCookie = "ddb_vid";
const biSessionIdCookie = "ddb_sid";
const getCookie = (name: string): string | undefined =>
  "document" in globalThis
    ? document.cookie.match(`${name}=([^;]*)`)?.[1]
    : undefined;
const getAwsRegion = (env?: string): Region =>
  env === "production" ? Region.UsEast1 : Region.UsEast2;
const getClientEventPipeline = (env?: string) =>
  new EventPipeline(getAwsRegion(env));
const getUserPlugin = (user: User | null): UserPlugin => {
  let subLevel: SubscriptionLevel = SubscriptionLevel.Visitor;

  if (!user) {
    return {
      userId: undefined,
      subscriptionLevel: subLevel,
      visitorId: getCookie(biVisitorIdCookie),
      sessionId: getCookie(biSessionIdCookie),
    };
  }

  if (user.subscriptionTier) {
    subLevel = user.subscriptionTier as SubscriptionLevel;
  } else if (user.roles?.some((x) => x === "Registered Users")) {
    subLevel = SubscriptionLevel.RegisteredUser;
  }

  return {
    userId: user.id,
    subscriptionLevel: subLevel,
    visitorId: getCookie(biVisitorIdCookie),
    sessionId: getCookie(biSessionIdCookie),
  };
};
const getGeoPlugin = (ipAddress: string, env?: string): GeoPlugin => ({
  region: getAwsRegion(env),
  countryCode: "",
  ipAddress: ipAddress,
});

export const sendEvent = async (
  user: User | null,
  messageName: string,
  eventPlugin: Plugin,
  platform: Platform,
  ipAddress?: string,
  env?: string,
) => {
  const plugins: Plugin[] = [getUserPlugin(user), eventPlugin];

  if (ipAddress) {
    plugins.push(getGeoPlugin(ipAddress, env));
  }

  if (isDebug) {
    console.log(
      `\n\nSEND_EVENT\n\n${JSON.stringify(
        {
          detailType: "ddb-maps",
          detail: {
            messageName,
            platform: platform ?? Platform.Web,
            plugins,
          },
          source: isMobileApp ? Source.Mobile : Source.Web,
        },
        null,
        2,
      )}\n\n`,
    );
  }

  if (process.env.NODE_ENV !== "development") {
    const eventSent = await getClientEventPipeline(env).sendEvent(
      {
        messageName,
        platform,
        plugins,
      },
      Source.Mobile,
    );

    if (!eventSent.success) {
      console.error(
        "Error sending event to the event pipeline: ",
        eventSent.errorMessages,
      );
    }
  }
};
